import { AxiosRequestConfig } from 'axios';
import PatientService from './PatientService';
import urls from '../util/urls';
import { ServiceAuth } from '../hooks/useService/useService';

export class ClinicalImpressionService {
  api: PatientService;
  constructor(options?: Partial<AxiosRequestConfig>) {
    this.api = new PatientService(options);
  }

  saveImpression =
    (auth: ServiceAuth) =>
    async (params: {
      patientId: string;
      projectId: string;
      impressionId: string;
      impression: fhir.ClinicalImpression;
    }) => {
      return await this.api.PatientAPI(auth).put<fhir.ClinicalImpression>(
        urls.api.fhir.clinicalImpression({
          account: auth.account,
          id: params.impressionId,
        }),
        {
          ...params.impression,
        },
      );
    };

  /**
   * Reads a single ClinicalImpression by id. Unlike `listImpressions`, ABAC is
   * enforced against the actual fetched resource's attributes rather than a
   * "prototype" resource guessed from search terms, and a missing/inaccessible
   * resource reliably surfaces as a 404 instead of an empty search bundle.
   * Prefer this over `listImpressions({ _id })` for existence checks.
   */
  getImpression =
    (auth: ServiceAuth) => async (params: { cohort?: string; id: string }) => {
      return await this.api.PatientAPI(auth).get<fhir.ClinicalImpression>(
        urls.api.fhir.clinicalImpression({
          account: auth.account,
          id: params.id,
          params: { cohort: params.cohort },
        }),
      );
    };

  listImpressions =
    (auth: ServiceAuth) =>
    async (params: {
      patientId: string;
      cohort?: string;
      projectId?: string;
      [key: string]: string | number | undefined;
    }) => {
      return await this.api.PatientAPI(auth).post<fhir.Bundle>(
        urls.api.fhir.clinicalImpression({
          isPostQuery: true,
          account: auth.account,
        }),
        {
          ...params,
          subject: params.patientId,
          cohort: params.cohort,
          _tag: [
            `http://lifeomic.com/fhir/dataset|${params.projectId}`,
            ...(params._tag ? [params._tag] : []),
          ],
        },
      );
    };

  deleteImpression =
    (auth: ServiceAuth) =>
    async (params: { patientId: string; impressionId: string }) => {
      return await this.api.PatientAPI(auth).delete<fhir.ClinicalImpression>(
        urls.api.fhir.clinicalImpression({
          account: auth.account,
          id: params.impressionId,
        }),
      );
    };
}
